import { PositiveInteger, NonNegativeInteger } from "../scalars.ts";
import { Schema } from "effect";
import { Sha256Identity } from "../../identity-input.ts";
import { FindingCategory, FindingSeverity } from "../change-review/model.ts";
import { StationRole } from "../../development-authorization.ts";
import { CandidateId, EvidenceId, FindingId, PrincipalId } from "../../development-identifiers.ts";
export { FindingDispositionError } from "../../errors/review.ts";
export { FindingDispositionDependencyError, findingDispositionDependencyError } from "../../errors/finding-disposition.ts";

export const FINDING_DISPOSITION_VERSION = 1 as const;

export const FindingLifecycleState = /* @__PURE__ */ (() => Schema.Literals(["draft", "open", "addressed", "resolved", "dismissed", "reopened"]))();
export type FindingLifecycleState = typeof FindingLifecycleState.Type;

export const FindingDispositionAction = /* @__PURE__ */ (() => Schema.Literals(["open", "address", "resolve", "dismiss", "reopen"]))();
export type FindingDispositionAction = typeof FindingDispositionAction.Type;

export const FindingGateEffect = /* @__PURE__ */ (() => Schema.Literals(["blocking", "advisory"]))();
export type FindingGateEffect = typeof FindingGateEffect.Type;
export const FindingActorScope = /* @__PURE__ */ (() => Schema.Literals(["assigned-author", "fix-runner", "station-role"]))();
export type FindingActorScope = typeof FindingActorScope.Type;

export { PositiveInteger } from "../scalars.ts";
export const DispositionTime = NonNegativeInteger;
export const StableToken = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9._:-]{0,127}$/)))();
export const DispositionReason = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isMinLength(1), Schema.isMaxLength(2_000),
  Schema.makeFilter(value => value.trim() === value && !/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/.test(value))
))();
export const FindingDispositionId = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^disposition:v1:sha256:[0-9a-f]{64}$/)))();
export const isFindingDispositionId = /* @__PURE__ */ (() => Schema.is(FindingDispositionId))();

export const FindingAuthorityGrantInput = /* @__PURE__ */ (() => Schema.Struct({
  principalId: PrincipalId.pipe(Schema.mutableKey),
  role: StationRole.pipe(Schema.mutableKey),
  revision: PositiveInteger.pipe(Schema.mutableKey),
  revoked: Schema.Boolean.pipe(Schema.optional, Schema.mutableKey),
}))();
export type FindingAuthorityGrantInput = typeof FindingAuthorityGrantInput.Encoded;

export const FindingDispositionSnapshotInput = /* @__PURE__ */ (() => Schema.Struct({
  findingId: FindingId.pipe(Schema.mutableKey),
  candidateId: CandidateId.pipe(Schema.mutableKey),
  lifecycle: FindingLifecycleState.pipe(Schema.mutableKey),
  severity: FindingSeverity.pipe(Schema.mutableKey),
  category: FindingCategory.pipe(Schema.mutableKey),
  gateEffect: FindingGateEffect.pipe(Schema.mutableKey),
}))();
export type FindingDispositionSnapshotInput = typeof FindingDispositionSnapshotInput.Encoded;

export const FindingDispositionInput = /* @__PURE__ */ (() => Schema.Struct({
  finding: FindingDispositionSnapshotInput.pipe(Schema.mutableKey),
  action: FindingDispositionAction.pipe(Schema.mutableKey),
  actor: FindingAuthorityGrantInput.pipe(Schema.mutableKey),
  actorScope: FindingActorScope.pipe(Schema.mutableKey),
  assignmentVerified: Schema.Boolean.pipe(Schema.optional, Schema.mutableKey),
  secondActor: FindingAuthorityGrantInput.pipe(Schema.optional, Schema.mutableKey),
  reasonCode: StableToken.pipe(Schema.mutableKey),
  reasonDetail: DispositionReason.pipe(Schema.mutableKey),
  evidenceId: EvidenceId.pipe(Schema.optional, Schema.mutableKey),
  createdAt: DispositionTime.pipe(Schema.mutableKey),
}))();
export type FindingDispositionInput = typeof FindingDispositionInput.Encoded;

export const FindingDispositionSigner = /* @__PURE__ */ (() => Schema.Struct({
  principalId: PrincipalId,
  grantRevision: PositiveInteger,
}))();
export type FindingDispositionSigner = typeof FindingDispositionSigner.Type;

export const FindingDispositionArtifact = /* @__PURE__ */ (() => Schema.Struct({
  version: Schema.Literal(FINDING_DISPOSITION_VERSION),
  dispositionId: FindingDispositionId,
  artifactDigest: Sha256Identity,
  findingId: FindingId,
  candidateId: CandidateId,
  action: FindingDispositionAction,
  priorState: FindingLifecycleState,
  resultingState: FindingLifecycleState,
  severity: FindingSeverity,
  category: FindingCategory,
  gateEffect: FindingGateEffect,
  actorScope: FindingActorScope,
  actor: FindingDispositionSigner,
  secondActor: FindingDispositionSigner.pipe(Schema.optional),
  safetyQuorum: Schema.Boolean,
  reasonCode: StableToken,
  reasonDetail: DispositionReason,
  evidenceId: EvidenceId.pipe(Schema.optional),
  createdAt: DispositionTime,
}))();
export type FindingDispositionArtifact = typeof FindingDispositionArtifact.Type;

/** Identity fields use the exact artifact validators, excluding the two fields
 * that can only be checked after hashing. Unknown fields are excluded here and
 * rejected by the final canonical equality check. */
export const FindingDispositionIdentity = /* @__PURE__ */ (() => {
  const { dispositionId: _id, artifactDigest: _digest, ...fields } = FindingDispositionArtifact.fields;
  return Schema.Struct(fields).check(Schema.makeFilter(value => {
    const safety = (value.action === "resolve" || value.action === "dismiss") && value.gateEffect === "blocking" &&
      (value.severity === "critical" || value.category === "security");
    return (value.actorScope === "station-role" || value.action === "address") &&
      (!(value.action === "address" || value.action === "resolve") || value.evidenceId !== undefined) &&
      (safety ? value.secondActor !== undefined && value.secondActor.principalId !== value.actor.principalId : value.secondActor === undefined) &&
      value.safetyQuorum === safety;
  }));
})();
export type FindingDispositionIdentity = typeof FindingDispositionIdentity.Type;
